import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import DecisionBrief from '@/components/decisions/DecisionBrief';
import { RiskSignalRow, humanizeRule, humanizeKey, formatEvidenceValue, getSeverityBadgeColor } from './riskUtils';

// The "Why" tab for a project: adapts DecisionBrief (built for a single
// signal) into an aggregate list across this project's open signals — each
// finding either opens its own AI evidence brief, or shows "Brief pending"
// when evaluate-risk-rules hasn't generated one yet (e.g. AI gateway error
// on that pass).
export const RiskWhyPanel = ({ signals, onDecided }: { signals: RiskSignalRow[]; onDecided: () => void }) => {
  const [openBriefId, setOpenBriefId] = useState<string | null>(null);

  if (signals.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
        <ShieldAlert className="h-8 w-8 mb-3 opacity-40" />
        <p className="text-sm font-medium text-foreground">No open findings for this project</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {signals.map((signal) => (
        <div key={signal.id} className="p-4 rounded-lg border bg-card">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <Badge className={cn('text-xs', getSeverityBadgeColor(signal.severity))}>{signal.severity}</Badge>
              <span className="font-medium text-sm">{humanizeRule(signal.rule_id)}</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
              <Clock className="h-3 w-3" />
              {new Date(signal.detected_at).toLocaleString()}
            </div>
          </div>
          <ul className="text-sm text-muted-foreground space-y-1 mb-3">
            {Object.entries(signal.evidence).map(([k, v]) => (
              <li key={k}>
                <span className="font-medium text-foreground">{humanizeKey(k)}:</span> {formatEvidenceValue(v)}
              </li>
            ))}
          </ul>
          {signal.ai_output_id ? (
            <Button size="sm" variant="outline" onClick={() => setOpenBriefId(signal.ai_output_id)}>
              Review AI Brief
            </Button>
          ) : (
            <Badge variant="outline" className="text-xs text-muted-foreground">Brief pending</Badge>
          )}
        </div>
      ))}

      <DecisionBrief
        aiOutputId={openBriefId}
        open={openBriefId !== null}
        onOpenChange={(open) => { if (!open) setOpenBriefId(null); }}
        onDecided={() => { setOpenBriefId(null); onDecided(); }}
      />
    </div>
  );
};

export default RiskWhyPanel;
